import React, { useCallback, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  LMI_MAX_RADIUS_MILES,
  LMI_MIN_RADIUS_MILES,
  LMI_MOCK_CPCS,
  LMI_MOCK_GOOGLE_AD_KEYWORDS,
  LMI_MOCK_KEYWORDS,
} from '../../growth-local-market/mockData';
import { DEFAULT_GOOGLE_MAPS_API_KEY } from '../../growth-local-market/config';
import type {
  LocalMarketCompetitor,
  LocalMarketCompetitorDetail,
  LocalMarketDrawerTab,
  LocalMarketKeywordsData,
} from '../../growth-local-market/types';
import { Button } from '../Button/Button';
import { Card } from '../Card/Card';
import { Icon } from '../Icon/Icon';
import { Pill } from '../Pill/Pill';
import { ViewportDrawer } from '../Overlay/ViewportDrawer';
import { Segmented } from '../Segmented/Segmented';
import { Slider } from '../Slider/Slider';
import { GoogleLocalMarketMap } from './GoogleLocalMarketMap';

export interface LocalMarketIntelligenceViewProps {
  clinicName: string;
  clinicAddress: string;
  centerCoordinate?: { lat: number; lng: number } | null;
  googleMapsApiKey?: string;
  competitors?: LocalMarketCompetitor[];
  keywordOpportunities?: LocalMarketKeywordsData;
  analysisRadius?: number;
  onAnalysisRadiusChange?: (radius: number) => void;
  loading?: boolean;
  onGenerateAds?: () => void;
  competitorDetail?: LocalMarketCompetitorDetail;
  competitorDetailLoading?: boolean;
  onCompetitorSelect?: (id: string | null) => void;
}

const DRAWER_TABS: { value: LocalMarketDrawerTab; label: string }[] = [
  { value: 'google-ads', label: 'Google Ads' },
  { value: 'google-ads-seo-organic', label: 'SEO & Organic' },
];

export function LocalMarketIntelligenceView({
  clinicName,
  clinicAddress,
  centerCoordinate = null,
  googleMapsApiKey,
  competitors: competitorsProp,
  keywordOpportunities,
  analysisRadius: analysisRadiusProp,
  onAnalysisRadiusChange,
  loading = false,
  onGenerateAds,
  competitorDetail,
  competitorDetailLoading = false,
  onCompetitorSelect,
}: LocalMarketIntelligenceViewProps) {
  const [localRadius, setLocalRadius] = useState(5);
  const analysisRadius = analysisRadiusProp ?? localRadius;
  const setAnalysisRadius = onAnalysisRadiusChange ?? setLocalRadius;
  const controlled = competitorsProp !== undefined;
  const [mapCompetitors, setMapCompetitors] = useState<LocalMarketCompetitor[]>([]);
  const competitors = controlled ? competitorsProp : mapCompetitors;
  const [selectedCompetitor, setSelectedCompetitor] = useState<LocalMarketCompetitor | null>(
    null,
  );
  const [drawerTab, setDrawerTab] = useState<LocalMarketDrawerTab>('google-ads');

  const handleCompetitorViewMore = useCallback(
    (competitor: LocalMarketCompetitor) => {
      setDrawerTab('google-ads');
      setSelectedCompetitor(competitor);
      onCompetitorSelect?.(competitor.id);
    },
    [onCompetitorSelect],
  );

  const closeDrawer = useCallback(() => {
    setSelectedCompetitor(null);
    onCompetitorSelect?.(null);
  }, [onCompetitorSelect]);

  const drawerRating =
    competitorDetail?.rating ?? selectedCompetitor?.rating;
  const drawerReviewCount =
    competitorDetail?.reviewCount ?? selectedCompetitor?.reviewCount;
  const googleAds = competitorDetail?.googleAds;
  const seoDetail = competitorDetail?.seo;
  const activeAd = googleAds?.activeAds?.[0];
  const adKeywords =
    googleAds?.topKeywords ??
    selectedCompetitor?.adCategories?.map((label) => ({ label, tone: 'success' as const })) ??
    LMI_MOCK_GOOGLE_AD_KEYWORDS;

  return (
    <div className="relative mx-auto w-full max-w-[1180px] px-[30px] pb-[90px] pt-[26px]">
      <h1 className="m-0 font-[var(--font-display)] text-[22px] font-medium leading-tight tracking-[-0.025em] text-[var(--ink-0)]">
        Local Market Intelligence
      </h1>
      <p className="mt-1 text-sm text-[var(--ink-2)]">
        Real-time competitive analysis for your practice location
        {loading ? ' · Updating…' : ''}
      </p>

      <div className="mt-6 flex flex-col gap-4 rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--surface-mute)] p-3 xl:flex-row xl:items-center xl:gap-6">
        <div className="flex min-w-0 items-center gap-3 xl:min-w-[220px] xl:border-r xl:border-[var(--cream-2)] xl:pr-6">
          <Icon kind="pin" size={20} stroke={2} className="shrink-0 text-[var(--success)]" />
          <div className="min-w-0">
            <p className="text-xs text-[var(--ink-3)]">Center Point</p>
            <p className="truncate text-sm font-semibold text-[var(--ink-0)]">{clinicName}</p>
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <div className="mb-4 flex items-center justify-between gap-4">
            <p className="text-xs text-[var(--ink-2)]">Analysis Radius</p>
            <p className="text-sm font-medium text-[var(--success)]">
              {analysisRadius} Mile Radius
            </p>
          </div>
          <Slider
            min={LMI_MIN_RADIUS_MILES}
            max={LMI_MAX_RADIUS_MILES}
            value={analysisRadius}
            onChange={setAnalysisRadius}
          />
        </div>
      </div>

      <GoogleLocalMarketMap
        analysisRadiusMiles={analysisRadius}
        clinicAddress={clinicAddress}
        clinicName={clinicName}
        centerCoordinate={centerCoordinate}
        googleMapsApiKey={googleMapsApiKey ?? DEFAULT_GOOGLE_MAPS_API_KEY}
        {...(competitorsProp ? { competitors: competitorsProp } : {})}
        {...(!controlled ? { onCompetitorsChange: setMapCompetitors } : {})}
        onCompetitorViewMore={handleCompetitorViewMore}
      />

      <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-2 2xl:grid-cols-[1.05fr_1.3fr]">
        <Card className="overflow-hidden !p-0" padding={0}>
          <div className="flex items-center justify-between border-b border-[var(--cream-2)] px-4 py-4">
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-[var(--success-tint)] p-2.5 text-[var(--success)]">
                <Icon kind="star" size={18} stroke={2} />
              </span>
              <h2 className="m-0 font-[var(--font-display)] text-[15px] font-bold leading-snug tracking-[-0.02em] text-[var(--ink-0)]">
                Top Local Competitors
              </h2>
            </div>
            <button
              type="button"
              className="cursor-pointer border-0 bg-transparent text-sm font-medium text-[var(--success)]"
            >
              View All Market Data
            </button>
          </div>

          <div className="overflow-x-auto">
            <div className="min-w-[680px]">
              <div className="grid grid-cols-[1.6fr_1fr_1.3fr] border-b border-[var(--cream-2)] bg-[var(--surface-mute)] px-4 py-3 text-xs font-medium text-[var(--ink-3)]">
                <span>Clinic Name</span>
                <span>Est. Spend</span>
                <span>Active Ad Categories</span>
              </div>

              <div className="max-h-[360px] overflow-y-auto">
                {competitors.length > 0 ? (
                  competitors.map((competitor, index) => (
                    <div
                      key={competitor.id}
                      className="grid grid-cols-[1.6fr_1fr_1.3fr] items-center gap-4 border-b border-[var(--cream-2)] px-4 py-4 last:border-b-0"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-base font-semibold text-[var(--ink-0)]">
                          {competitor.name}
                        </p>
                        <p className="mt-0.5 text-sm text-[var(--ink-1)]">
                          {competitor.distanceMiles.toFixed(1)} mi away
                        </p>
                      </div>
                      <p className="text-base font-semibold text-[var(--ink-0)]">
                        {competitor.estMonthlySpend ?? '—'}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {(competitor.adCategories?.length
                          ? competitor.adCategories
                          : ['—']
                        ).map((category) => (
                          <Pill key={category} tone={category === '—' ? 'info' : 'success'}>
                            {category}
                          </Pill>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-4 py-8 text-sm text-[var(--ink-3)]">
                    No local competitors found for this radius.
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>

        <Card
          className="flex h-full flex-col overflow-hidden border-[color-mix(in_srgb,var(--success)_18%,transparent)] bg-[var(--success-tint)] !p-0"
          padding={0}
        >
          <div className="flex items-center justify-between px-4 py-4">
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-[var(--cream-1)] p-2.5 text-[var(--success)]">
                <Icon kind="sparkle" size={17} stroke={2} />
              </span>
              <h2 className="m-0 font-[var(--font-display)] text-[15px] font-bold leading-snug tracking-[-0.02em] text-[var(--ink-0)]">
                AI Keyword Opportunities
              </h2>
            </div>
            <Pill tone="success">High Impact</Pill>
          </div>

          <div className="flex flex-1 flex-col px-4 pb-4">
            <div className="max-h-[400px] flex-1 overflow-y-auto">
              <div className="rounded-[var(--r-xl)] bg-[var(--cream-1)] p-4 text-base text-[var(--ink-2)]">
                {keywordOpportunities?.narrative ??
                  'Keyword opportunities will appear when analysis is available.'}
              </div>

              <div className="mt-4">
                <p className="text-sm font-semibold text-[var(--ink-3)]">Recommended Strategy</p>
                <div className="mt-4 space-y-3">
                  {(keywordOpportunities?.recommended?.length
                    ? keywordOpportunities.recommended.map((row) => row.keyword)
                    : LMI_MOCK_KEYWORDS
                  ).map((keyword, index) => (
                    <div
                      key={keyword}
                      className="flex items-center justify-between rounded-[var(--r-xl)] border border-[color-mix(in_srgb,var(--success)_22%,transparent)] bg-[var(--cream-1)] px-4 py-3"
                    >
                      <div className="flex items-center gap-2.5">
                        <Icon kind="check-circ" size={17} stroke={2} className="text-[var(--success)]" />
                        <span className="text-sm font-semibold text-[var(--ink-2)]">{keyword}</span>
                      </div>
                      <span className="text-xs font-semibold text-[var(--success)]">
                        {keywordOpportunities?.recommended?.[index]?.volume != null
                          ? `${keywordOpportunities.recommended[index].volume.toLocaleString()}/mo`
                          : LMI_MOCK_CPCS[index]}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="pt-6">
              <Button
                type="button"
                variant="primary"
                size="lg"
                iconLeft="sparkle"
                className="mx-auto w-full max-w-[520px]"
                onClick={onGenerateAds}
              >
                Generate Ads for these Keywords
              </Button>
            </div>
          </div>
        </Card>
      </div>

      <ViewportDrawer
        open={Boolean(selectedCompetitor)}
        onClose={closeDrawer}
        width="min(100vw, 690px)"
        panelClassName="bg-[var(--cream-1)]"
      >
        {selectedCompetitor ? (
          <div className="flex h-full flex-col">
            <div className="border-b border-[var(--cream-2)] px-6 py-8">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="m-0 text-[20px] font-semibold text-[var(--ink-0)]">
                    {selectedCompetitor.name}
                  </h2>
                  <p className="mt-1 flex items-center gap-1 text-xs text-[var(--ink-3)]">
                    {selectedCompetitor.distanceMiles.toFixed(1)} miles away
                    {competitorDetailLoading ? <span>· Loading details…</span> : null}
                    {drawerRating != null ? (
                      <>
                        <span>·</span>
                        <Icon kind="star" size={10} stroke={2} className="text-[var(--gold)]" />
                        {drawerRating}
                        {drawerReviewCount != null
                          ? ` (${drawerReviewCount} Google Reviews)`
                          : null}
                      </>
                    ) : null}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeDrawer}
                  className="cursor-pointer rounded-[var(--r-md)] border-0 bg-transparent p-1 text-[var(--ink-1)] transition hover:bg-[var(--surface-mute)]"
                  aria-label="Close drawer"
                >
                  <Icon kind="x" size={20} stroke={2} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-5">
              <Segmented
                className="w-full"
                options={DRAWER_TABS}
                value={drawerTab}
                onChange={(value) => setDrawerTab(value as LocalMarketDrawerTab)}
              />

              <AnimatePresence mode="wait" initial={false}>
                {drawerTab === 'google-ads' ? (
                  <motion.div
                    key="google-ads"
                    initial={{ opacity: 0, y: 18, scale: 0.985 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -14, scale: 0.985 }}
                    transition={{ duration: 0.22, ease: 'easeOut' }}
                  >
                    <div className="mt-4 grid grid-cols-2 gap-3">
                      <Card className="!p-4">
                        <p className="text-sm text-[var(--ink-3)]">Est. Ad Spend</p>
                        <div className="mt-3 flex items-center gap-1">
                          <span className="text-[30px] font-semibold text-[var(--ink-0)]">
                            {googleAds?.estMonthlySpend?.replace(/^\$/, '') ??
                              selectedCompetitor.estMonthlySpend?.replace(/^\$/, '') ??
                              '—'}
                          </span>
                          {(googleAds?.estMonthlySpend ?? selectedCompetitor.estMonthlySpend) ? (
                            <span className="text-[20px] text-[var(--ink-0)]">/mo</span>
                          ) : null}
                          {googleAds?.spendDeltaPct != null ? (
                            <Pill tone="success">{googleAds.spendDeltaPct}%</Pill>
                          ) : null}
                        </div>
                      </Card>
                      <Card className="!p-4">
                        <p className="text-sm text-[var(--ink-3)]">Local SEO Rank</p>
                        <div className="mt-3 flex items-center gap-2">
                          <span className="text-[30px] font-semibold text-[var(--ink-0)]">
                            {googleAds?.localSeoRank != null ? `#${googleAds.localSeoRank}` : '—'}
                          </span>
                        </div>
                      </Card>
                    </div>

                    <div className="mt-5">
                      <h4 className="text-sm font-semibold text-[var(--ink-0)]">Active Google Ads</h4>
                      <div className="mt-3 rounded-[var(--r-xl)] bg-[var(--surface-mute)] p-4">
                        {activeAd ? (
                          <>
                            <div className="flex items-center gap-3 text-xs text-[var(--ink-3)]">
                              <span className="rounded-[var(--r-sm)] bg-[var(--cream-1)] px-2 py-1 font-semibold text-[var(--ink-2)]">
                                AD
                              </span>
                              {activeAd.displayUrl ? <span>{activeAd.displayUrl}</span> : null}
                            </div>
                            {activeAd.headline ? (
                              <p className="mt-4 text-[16px] font-medium text-[var(--success)]">
                                {activeAd.headline}
                              </p>
                            ) : null}
                            {activeAd.body ? (
                              <p className="mt-2 text-sm leading-6 text-[var(--ink-3)]">{activeAd.body}</p>
                            ) : null}
                          </>
                        ) : (
                          <p className="text-sm text-[var(--ink-3)]">
                            No active ad copy available from the pipeline yet.
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="mt-5">
                      <h4 className="text-sm font-semibold text-[var(--ink-0)]">Top Targeted Keywords</h4>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {adKeywords.map((keyword) => (
                          <Pill key={keyword.label} tone={keyword.tone ?? 'success'}>
                            {keyword.label}
                          </Pill>
                        ))}
                      </div>
                    </div>

                    <div className="mt-6 rounded-[10px] border border-[color-mix(in_srgb,var(--ai-3)_24%,transparent)] bg-[var(--ai-3)] p-5 text-[var(--cream-1)]">
                      <div className="flex items-start gap-4">
                        <Icon kind="sparkle" size={17} stroke={2} className="shrink-0 text-white" />
                        <div>
                          <h3 className="m-0 font-[var(--font-display)] text-[15px] font-bold tracking-[-0.02em]">
                            ELVA Counter-Strategy
                          </h3>
                          <p className="mt-4 text-sm font-medium leading-4">
                            {googleAds?.counterStrategy ??
                              `${selectedCompetitor.name} competitive ad strategy will appear when pipeline data is available.`}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="google-ads-seo-organic"
                    initial={{ opacity: 0, y: 18, scale: 0.985 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -14, scale: 0.985 }}
                    transition={{ duration: 0.22, ease: 'easeOut' }}
                  >
                    <div className="mt-4 grid grid-cols-2 gap-3">
                      <Card className="!p-4">
                        <p className="text-sm text-[var(--ink-3)]">Est. Organic Traffic</p>
                        <div className="mt-3 flex items-center gap-2">
                          <span className="text-[30px] font-semibold text-[var(--ink-0)]">
                            {seoDetail?.estOrganicTraffic != null
                              ? seoDetail.estOrganicTraffic.toLocaleString()
                              : '—'}
                          </span>
                          {seoDetail?.estOrganicTraffic != null ? (
                            <span className="text-[20px] text-[var(--ink-0)]">/mo</span>
                          ) : null}
                        </div>
                      </Card>
                      <Card className="!p-4">
                        <p className="text-sm text-[var(--ink-3)]">Local SEO Score</p>
                        <div className="mt-3 flex items-center gap-2">
                          <span className="text-[30px] font-semibold text-[var(--ink-0)]">
                            {seoDetail?.localSeoScore != null ? (
                              <>
                                {seoDetail.localSeoScore}{' '}
                                <span className="text-[20px] font-medium">/100</span>
                              </>
                            ) : (
                              '—'
                            )}
                          </span>
                        </div>
                      </Card>
                    </div>

                    <div className="mt-6">
                      <h4 className="text-base font-semibold text-[var(--ink-0)]">
                        Top Ranked Organic Keywords
                      </h4>
                      <div className="mt-3 space-y-2">
                        {(seoDetail?.rankedKeywords?.length
                          ? seoDetail.rankedKeywords
                          : []
                        ).map((row) => (
                          <div
                            key={row.keyword}
                            className="flex items-center justify-between rounded-[var(--r-xl)] bg-[var(--surface-mute)] px-4 py-3"
                          >
                            <span className="text-sm font-semibold text-[var(--ink-2)]">
                              {row.keyword}
                            </span>
                            <div className="flex items-center gap-2">
                              <Pill tone="success">Rank #{row.rank}</Pill>
                              <Icon kind="arrow-r" size={16} stroke={2.2} className="text-[var(--success)]" />
                            </div>
                          </div>
                        ))}
                        {!seoDetail?.rankedKeywords?.length ? (
                          <p className="text-sm text-[var(--ink-3)]">
                            Organic keyword rankings will appear when pipeline data is available.
                          </p>
                        ) : null}
                      </div>
                    </div>

                    <div className="mt-5">
                      <h4 className="text-base font-semibold text-[var(--ink-0)]">Website Health Audit</h4>
                      <div className="mt-3 space-y-3">
                        {(seoDetail?.websiteHealth ?? []).map((check) => (
                          <div
                            key={check.label}
                            className="flex items-center gap-2 text-base text-[var(--ink-2)]"
                          >
                            <Icon
                              kind={check.status === 'good' ? 'check-circ' : 'alert'}
                              size={16}
                              stroke={2}
                              className={
                                check.status === 'good'
                                  ? 'text-[var(--success)]'
                                  : 'text-[var(--terracotta)]'
                              }
                            />
                            <span className="font-semibold">
                              {check.label}: <span className="font-normal">{check.value}</span>
                            </span>
                          </div>
                        ))}
                        {!seoDetail?.websiteHealth?.length ? (
                          <p className="text-sm text-[var(--ink-3)]">
                            Website health checks will appear when pipeline data is available.
                          </p>
                        ) : null}
                      </div>
                    </div>

                    <div className="mt-6 rounded-[10px] border border-[color-mix(in_srgb,var(--ai-3)_24%,transparent)] bg-[var(--ai-3)] p-5 text-[var(--cream-1)]">
                      <div className="flex items-start gap-4">
                        <Icon kind="sparkle" size={17} stroke={2} className="shrink-0 text-white" />
                        <div>
                          <h3 className="m-0 font-[var(--font-display)] text-[15px] font-bold tracking-[-0.02em]">
                            ELVA SEO Opportunity
                          </h3>
                          <p className="mt-4 text-sm font-medium leading-4">
                            {seoDetail?.seoOpportunity ??
                              `${selectedCompetitor.name} SEO opportunities will appear when pipeline data is available.`}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="border-t border-[var(--cream-2)] p-5">
              <Button
                type="button"
                variant="primary"
                size="lg"
                iconLeft="sparkle"
                className="w-full"
              >
                {drawerTab === 'google-ads'
                  ? 'Generate Counter-Campaign'
                  : 'Generate SEO Landing Page'}
              </Button>
            </div>
          </div>
        ) : null}
      </ViewportDrawer>
    </div>
  );
}
